<section class="rounded-lg border border-[var(--p-surface-200)] bg-[var(--p-surface-0)] shadow-sm" aria-label="Calculation Control">
  <!-- Header bar -->
  <header class="row-between-center gap-3 px-4 py-3 border-b border-[var(--p-surface-200)]">
    <div class="row-start-center gap-3">
      <i class="pi pi-bolt text-[var(--p-primary-color)]"></i>
      <h2 class="text-base font-semibold text-[var(--p-text-color)]">
        {{ 'Cost_Analysis_Calc_Control_Title' | translateWF: 'Calculation Control' }}
      </h2>
      <span class="inline-flex items-center rounded-full bg-[var(--p-surface-100)] px-2 py-0.5 text-xs font-medium text-[var(--p-text-muted-color)] tabular-nums">
        {{ year() }}-{{ month() < 10 ? '0' + month() : month() }}
      </span>
    </div>
    <button
      type="button"
      class="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--p-text-muted-color)] hover:bg-[var(--p-surface-100)] hover:text-[var(--p-text-color)]"
      (click)="toggleCollapse()"
      [attr.aria-label]="collapsed() ? ('Cost_Analysis_Expand' | translateWF: 'Expand') : ('Cost_Analysis_Collapse' | translateWF: 'Collapse')"
    >
      <i [class]="collapsed() ? 'pi pi-chevron-down' : 'pi pi-chevron-up'"></i>
    </button>
  </header>

  @if (!collapsed()) {
    <div class="p-4 space-y-4">
      <!-- Inputs row -->
      <div class="grid-12">
        <div class="span-12-4">
          <label class="block text-xs font-medium uppercase tracking-wider text-[var(--p-text-muted-color)] mb-1">
            {{ 'Cost_Analysis_Exchange_Rate_HNL_USD' | translateWF: 'Exchange Rate (HNL / USD)' }}
          </label>
          <p-inputNumber
            [formControl]="exchangeRateControl"
            [minFractionDigits]="2"
            [maxFractionDigits]="6"
            [min]="0"
            styleClass="w-full"
            [placeholder]="'Cost_Analysis_Exchange_Rate_Placeholder' | translateWF: '24.85'"
          />
          @if (exchangeRateControl.invalid && exchangeRateControl.touched) {
            <p class="mt-1 text-xs text-[var(--p-red-600)]">
              {{ 'Cost_Analysis_Exchange_Rate_Required' | translateWF: 'Required.' }}
            </p>
          }
        </div>
        <div class="span-12-4">
          <label class="block text-xs font-medium uppercase tracking-wider text-[var(--p-text-muted-color)] mb-1">
            {{ 'Cost_Analysis_Exchange_Rate_EUR_USD' | translateWF: 'Exchange Rate (EUR / USD)' }}
          </label>
          <p-inputNumber [formControl]="exchangeRateEurUsdControl" [minFractionDigits]="2" [maxFractionDigits]="6" [min]="0" styleClass="w-full" />
        </div>
      </div>

      <!-- Action row -->
      <div class="flex flex-wrap items-center gap-2 border-t border-[var(--p-surface-200)] pt-4">
        <p-button
          severity="secondary"
          icon="pi pi-search"
          [label]="'Cost_Analysis_Check_Prerequisites' | translateWF: 'Check Prerequisites'"
          [loading]="checkingPrereqs()"
          [disabled]="running()"
          (click)="checkPrerequisites()"
        />
        <p-button icon="pi pi-bolt" [label]="'Cost_Analysis_Calculate_All' | translateWF: 'Calculate All'" [loading]="running()" (click)="calculateAll()" />

        <!-- [disabled]="!canCalculate()" -->
        <span class="ml-auto row-start-center text-xs text-[var(--p-text-muted-color)]">
          @if (lastCheckedAt()) {
            <span>
              {{ 'Cost_Analysis_Last_Check' | translateWF: 'Last check' }}:
              <span class="tabular-nums">{{ lastCheckedAt() | date: 'HH:mm' }}</span>
            </span>
          }
          @if (lastRunAt()) {
            <span>
              {{ 'Cost_Analysis_Last_Run' | translateWF: 'Last run' }}:
              <span class="tabular-nums">{{ lastRunAt() | date: 'HH:mm' }}</span>
            </span>
          }
        </span>
      </div>

      <!-- Results table -->
      <div class="rounded-md border border-[var(--p-surface-200)]">
        <p-table [value]="rows()" [rowTrackBy]="trackByCode" [tableStyle]="{ 'min-width': '720px' }">
          <ng-template pTemplate="header">
            <tr>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_Process' | translateWF: 'Process' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_CostCenter' | translateWF: 'Cost Center' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_Status' | translateWF: 'Status' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold text-right">
                {{ 'Cost_Analysis_Col_Rolls' | translateWF: 'Rolls' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold text-right">
                {{ 'Cost_Analysis_Col_TotalCostUsd' | translateWF: 'Total Cost USD' }}
              </th>
              <th class="text-xs uppercase tracking-wider font-semibold">
                {{ 'Cost_Analysis_Col_Detail' | translateWF: 'Detail' }}
              </th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td class="font-medium text-[var(--p-text-color)]">{{ row.costCenter.label }}</td>
              <td class="font-mono text-xs text-[var(--p-text-muted-color)]">{{ row.costCenter.code }}</td>
              <td>
                <span [class]="chipClasses(row.status)">
                  <i [class]="chipIcon(row.status)" style="font-size: 0.75rem"></i>
                  @switch (row.status) {
                    @case ('idle') {
                      {{ 'Cost_Analysis_Status_Idle' | translateWF: 'Idle' }}
                    }
                    @case ('checking') {
                      {{ 'Cost_Analysis_Status_Checking' | translateWF: 'Checking' }}
                    }
                    @case ('ready') {
                      {{ 'Cost_Analysis_Status_Ready' | translateWF: 'Ready' }}
                    }
                    @case ('blocked') {
                      {{ 'Cost_Analysis_Status_Blocked' | translateWF: 'Blocked' }}
                    }
                    @case ('queued') {
                      {{ 'Cost_Analysis_Status_Queued' | translateWF: 'Queued' }}
                    }
                    @case ('running') {
                      {{ 'Cost_Analysis_Status_Running' | translateWF: 'Running' }}
                    }
                    @case ('success') {
                      {{ 'Cost_Analysis_Status_Success' | translateWF: 'Success' }}
                    }
                    @case ('failed') {
                      {{ 'Cost_Analysis_Status_Failed' | translateWF: 'Failed' }}
                    }
                  }
                </span>
              </td>
              <td class="text-right tabular-nums">
                @if (row.rollsProcessed !== undefined) {
                  <span class="text-[var(--p-text-color)]">{{ row.rollsProcessed }}</span>
                  <span class="text-[var(--p-text-muted-color)]"> / {{ row.rollsUpdated ?? 0 }}</span>
                } @else {
                  <span class="text-[var(--p-text-muted-color)]">—</span>
                }
              </td>
              <td class="text-right tabular-nums">
                @if (row.totalCostUsd !== undefined) {
                  ${{ row.totalCostUsd | number: '1.2-2' }}
                } @else {
                  <span class="text-[var(--p-text-muted-color)]">—</span>
                }
              </td>
              <td>
                @if (row.status === 'blocked' && row.missingItems.length > 0) {
                  <ul class="list-disc pl-4 text-xs text-[var(--p-red-700)] space-y-0.5">
                    @for (item of row.missingItems; track item) {
                      <li>{{ item }}</li>
                    }
                  </ul>
                } @else if (row.status === 'failed') {
                  <div class="text-xs text-[var(--p-red-700)]">
                    @if (row.errorCode) {
                      <span class="inline-block rounded bg-[var(--p-red-100)] px-1.5 py-0.5 font-mono text-[10px] mr-1">{{ row.errorCode }}</span>
                    }
                    <span>{{ row.errorMessage ?? ('Cost_Analysis_Unknown_Error' | translateWF: 'Unknown error.') }}</span>
                  </div>
                } @else {
                  <span class="text-[var(--p-text-muted-color)]">—</span>
                }
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center text-sm text-[var(--p-text-muted-color)] py-4">
                {{ 'Cost_Analysis_No_Rows' | translateWF: 'No rows.' }}
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  }
</section>
